import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bot, MessageSquare, Clock, Sparkles, ShieldCheck, ArrowRight, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import { CheckoutPromo } from "@/components/CheckoutPromo";

const BENEFITS = [
  { icon: MessageSquare, title: "Answers customers instantly", body: "Your own trained assistant handles questions about services, hours, pricing and location — day or night." },
  { icon: Clock, title: "Works 24/7", body: "It never sleeps, so leads get a reply the moment they land on your site — not hours later." },
  { icon: Sparkles, title: "Trained on your business", body: "We load your services, FAQs and tone so it sounds like you, not a generic bot." },
  { icon: ShieldCheck, title: "Fully managed", body: "We set it up, keep it accurate, and tune it every month. You just watch the conversations roll in." },
];

const STEPS = [
  ["1", "We learn your business", "Share your services, FAQs and the questions customers always ask."],
  ["2", "We build & train your assistant", "We create your AI Concierge in our platform and load your knowledge base."],
  ["3", "We add it to your site", "A tiny snippet goes on your website — your concierge is live and answering."],
  ["4", "We keep it sharp", "Optional monthly care keeps answers current as your business grows."],
];

const INPUT = "w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors";

export default function AIConciergePage() {
  const [form, setForm] = useState({ name: "", email: "", business_name: "", phone: "", goals: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [buying, setBuying] = useState(false);
  const [offer, setOffer] = useState(null);
  const [promo, setPromo] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/concierge/offer").then((r) => setOffer(r.data)).catch(() => {}); }, []);
  const founding = offer?.live;
  const bundlePrice = founding ? offer.price : 697;
  const finalPrice = promo ? Math.round(bundlePrice * (1 - promo.percent / 100)) : bundlePrice;

  const buyBundle = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/session", { kind: "concierge_bundle", origin_url: window.location.origin, discount_code: promo?.code });
      window.location.href = data.url;
    } catch (e) { toast.error("Could not start checkout — please try again."); setBuying(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      await api.post("/inquiries", {
        name: form.name.trim(), email: form.email.trim(), business_name: form.business_name.trim(),
        phone: form.phone.trim(), goals: form.goals.trim(),
        template_title: "AI Concierge", cta_source: "ai-concierge", action: "save",
      });
      setDone(true);
      toast.success("Thanks! We'll be in touch about your AI Concierge.");
    } catch (err) { toast.error("Something went wrong — please try again."); } finally { setSending(false); }
  };

  return (
    <div data-testid="ai-concierge-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
              <span className="h-px w-8 bg-gold" /> AI Concierge
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-3xl">
              Give your website its own <span className="text-gold">24/7 AI assistant.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              A friendly, always-on concierge trained on your business — answering customer questions, capturing leads, and pointing visitors to book or buy, so you never miss another one.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href="#get-started" data-testid="concierge-hero-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors">
                Get my AI Concierge <ArrowRight size={16} />
              </a>
              <span className="text-sm text-gray-400">From <span className="text-white font-semibold">$597</span> setup · <span className="text-white font-semibold">$99/mo</span> managed care</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="border border-white/10 bg-navy-800 p-6 hover:border-gold/40 transition-colors">
              <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold mb-4"><b.icon size={20} strokeWidth={1.6} /></div>
              <h3 className="text-white font-heading text-lg mb-2">{b.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{b.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-white/10 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <h2 className="font-heading text-3xl text-white mb-10">How it works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map(([n, t, d]) => (
              <div key={n} className="relative">
                <div className="text-gold/30 font-heading text-5xl mb-3">{n}</div>
                <h3 className="text-white font-semibold mb-1.5">{t}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example conversations */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="concierge-examples">
        <h2 className="font-heading text-3xl text-white mb-2">See it in action</h2>
        <p className="text-gray-400 text-sm mb-10 max-w-2xl">Real-world questions your AI Concierge handles instantly — so you capture the lead instead of losing it.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { tag: "Contractor", q: "Do you do metal roofs, and how soon can you come look?", a: "Yes — we install standing-seam and metal shingle roofs. I can get you on the schedule for a free estimate this week. What's the best number to reach you?" },
            { tag: "Bakery", q: "Can you make a gluten-free birthday cake for Saturday?", a: "Absolutely! We offer gluten-free cakes with 48 hours' notice. Want me to start your order? I just need the size, flavor, and pickup time." },
            { tag: "Church", q: "What time are Sunday services and where do I park?", a: "Sunday services are at 9:00 & 11:00 AM. Free parking is in the north lot off Main St, with greeters to help you find a seat. Hope to see you!" },
          ].map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="border border-white/10 bg-navy-800 p-5">
              <div className="text-[11px] uppercase tracking-wider text-gold mb-4">{c.tag}</div>
              <div className="space-y-3">
                <div className="flex justify-end">
                  <div className="bg-white/10 text-gray-200 text-sm px-3 py-2 rounded-lg rounded-br-none max-w-[85%]">{c.q}</div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold shrink-0"><Bot size={14} /></div>
                  <div className="bg-gold/10 text-gray-100 text-sm px-3 py-2 rounded-lg rounded-tl-none max-w-[85%]">{c.a}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing + Lead form */}
      <section id="get-started" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-heading text-3xl text-white mb-4">Simple, honest pricing</h2>
            <div className="border-2 border-gold bg-gold/10 p-6 mb-4 relative" data-testid="concierge-bundle-card">
              <span className="absolute -top-3 left-6 bg-gold text-navy text-[11px] font-bold uppercase tracking-wider px-2 py-0.5">{founding ? "Founding offer" : "Best value"}</span>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-4xl font-heading text-gold">${bundlePrice}</span>
                <span className="text-gray-300 text-sm line-through">${founding ? 697 : 894}</span>
                <span className="text-gray-400 text-sm">Launch Bundle</span>
              </div>
              <p className="text-gray-200 text-sm mt-2">Full setup <strong>plus your first 3 months of Concierge Care</strong> — get live and stay sharp{founding ? "" : ", and save $197"}.</p>
              {founding && (
                <p className="text-[12px] text-gold font-semibold mt-2" data-testid="concierge-founding-note">🔥 Founding price for our first {offer.slots} clients — only {offer.remaining} spot{offer.remaining === 1 ? "" : "s"} left.</p>
              )}
              <button onClick={buyBundle} disabled={buying} data-testid="concierge-buy-bundle"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                {buying ? <Loader2 className="animate-spin" size={15} /> : <ArrowRight size={15} />} Buy the bundle now — ${finalPrice}{promo ? <span className="line-through opacity-60 ml-1">${bundlePrice}</span> : null}
              </button>
              <div className="mt-3"><CheckoutPromo kind="concierge_bundle" applied={promo} onApply={setPromo} onClear={() => setPromo(null)} /></div>
              <p className="text-[11px] text-gray-500 mt-2 text-center">Pay securely online · we'll create your account and reach out to set it up.</p>
            </div>
            <div className="border border-gold/40 bg-gold/5 p-6 mb-4">
              <div className="flex items-baseline gap-2"><span className="text-4xl font-heading text-gold">$597</span><span className="text-gray-400 text-sm">one-time setup</span></div>
              <p className="text-gray-300 text-sm mt-2">We build, train and install your AI Concierge, ready to answer customers.</p>
            </div>
            <div className="border border-white/10 bg-navy-800 p-6">
              <div className="flex items-baseline gap-2"><span className="text-4xl font-heading text-white">$99</span><span className="text-gray-400 text-sm">/ month — Concierge Care</span></div>
              <ul className="mt-3 space-y-2">
                {["Monthly knowledge updates & tuning", "New answers as your business changes", "Conversation monitoring & improvements", "Priority support"].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-gray-300"><Check size={15} className="text-gold mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-4">Care is optional — cancel anytime. Setup gets you live either way.</p>
            </div>
          </div>

          {/* Lead form */}
          <div className="border border-white/10 bg-navy-800 p-6 md:p-8">
            {done ? (
              <div className="text-center py-10" data-testid="concierge-form-success">
                <div className="w-14 h-14 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mx-auto mb-4"><Check size={26} className="text-green-400" /></div>
                <h3 className="font-heading text-2xl text-white mb-2">You're on the list!</h3>
                <p className="text-gray-400 text-sm">We'll reach out shortly to set up your AI Concierge. Talk soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="concierge-lead-form">
                <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-1"><Bot size={14} /> Get your concierge</div>
                <h3 className="font-heading text-2xl text-white mb-2">Tell us about your business</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT} placeholder="Your name *" data-testid="concierge-name" />
                  <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT} placeholder="Email *" data-testid="concierge-email" />
                  <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={INPUT} placeholder="Business name" data-testid="concierge-business" />
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT} placeholder="Phone (optional)" data-testid="concierge-phone" />
                </div>
                <textarea value={form.goals} onChange={(e) => set("goals", e.target.value)} rows={3} className={INPUT + " resize-y"} placeholder="What should your concierge help customers with?" data-testid="concierge-message" />
                <button type="submit" disabled={sending} data-testid="concierge-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                  {sending ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />} Request my AI Concierge
                </button>
                <p className="text-xs text-gray-500 text-center">No obligation — we'll follow up with next steps and a quick quote.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
